import React from "react";
import { Card, Empty, ErrorState, Kpi, PageHeading, Rate, RowsSkeleton, Table } from "../ui";
import { Meter } from "../charts";
import { Icon } from "../icons";
import { useFilters } from "../filters";
import { useView } from "../data";
import { TASK_LABELS, fmt, pct, statusOf, usd, words } from "../format";

const BUCKETS = {
  good: { label: "Went well", hint: "every turn a success, or a request finished", color: "var(--aa-good)" },
  mixed: { label: "Partly", hint: "a turn was only partly done", color: "var(--aa-warn)" },
  bad: { label: "Went badly", hint: "a turn or a request failed", color: "var(--aa-bad)" },
  unfinished: { label: "Unfinished", hint: "left before anything finished", color: "var(--aa-neutral)" },
};

/** One cell of the cohort grid: the share returning, shaded on the sequential ramp. */
function CohortCell({ value }) {
  if (value == null) return <span style={{ color: "var(--aa-faint)" }} title="This week has not ended yet">·</span>;
  const step = value >= 0.6 ? 4 : value >= 0.35 ? 3 : value >= 0.15 ? 2 : 1;
  return (
    <span
      className="aa-num"
      style={{
        display: "inline-flex",
        justifyContent: "center",
        width: 64,
        padding: "6px 0",
        borderRadius: 6,
        background: `var(--aa-seq-${step})`,
        color: step >= 3 ? "#fff" : "var(--aa-ink)",
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      {pct(value)}
    </span>
  );
}

/** Do people come back after their first conversation — and does a good first one make the difference? */
export default function Outcomes() {
  const { params } = useFilters();
  const { data, loading, error } = useView("outcomes", params);
  const h = data?.headline ?? {};
  const retention = data?.retention ?? [];
  const cohorts = data?.cohorts ?? [];
  const story =
    h.goodRate7 != null && h.badRate7 != null
      ? `People whose first conversation went well came back within a week ${pct(h.goodRate7)} of the time, against ${pct(h.badRate7)} when it went badly.`
      : h.newUsers
        ? "Not enough finished first conversations yet to compare good and bad first experiences."
        : "No one used the agent for the first time in this period.";

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Retention & value">Whether new people come back, whether a good first conversation is why, and what each kind of request costs against what it delivers.</PageHeading>

      <div className="aa-grid aa-grid-kpi aa-kpi-3">
        <Kpi loading={loading} label="New people" value={fmt(h.newUsers)} foot="first request in this period" />
        <Kpi loading={loading} label="Back within 7 days" value={pct(h.rate7)} status={h.rate7 == null ? "none" : statusOf(h.rate7, { good: 0.4, bad: 0.2 })} foot="in a new conversation" />
        <Kpi loading={loading} label="Back within 28 days" value={pct(h.rate28)} status={h.rate28 == null ? "none" : statusOf(h.rate28, { good: 0.5, bad: 0.25 })} foot="people at least 28 days old" />
      </div>

      {!loading && (
        <div className="aa-card aa-fade" style={{ display: "flex", gap: 14, alignItems: "center", padding: "16px 20px", background: "var(--aa-accent-soft)", borderColor: "transparent" }}>
          <span className="aa-icon-tile" style={{ background: "var(--aa-accent)", color: "#fff", width: 32, height: 32 }}>
            <Icon name="sparkle" size={16} />
          </span>
          <div style={{ fontSize: 14, lineHeight: 1.55 }}>{story}</div>
        </div>
      )}

      <div className="aa-grid aa-grid-2">
        <Card title="Weekly cohorts" subtitle="Of each week's new people, the share who came back 1–4 weeks later" flush>
          {loading ? (
            <RowsSkeleton rows={5} />
          ) : error ? (
            <ErrorState error={error} />
          ) : cohorts.length ? (
            <Table
              columns={[
                { key: "week", label: "Week of", nowrap: true, render: (r) => new Date(r.week).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) },
                { key: "users", label: "People", align: "right", render: (r) => fmt(r.users) },
                ...[1, 2, 3, 4].map((k) => ({ key: `week${k}`, label: `Week ${k}`, align: "right", render: (r) => <CohortCell value={r[`week${k}`]} /> })),
              ]}
              rows={cohorts.map((r) => ({ ...r, id: r.week }))}
            />
          ) : (
            <Empty compact icon="people" title="No new people in this period" />
          )}
        </Card>

        <Card title="Does a good first conversation bring people back?" subtitle="Grouped by how their first conversation went" flush>
          {retention.some((b) => b.users) ? (
            <Table
              columns={[
                {
                  key: "bucket",
                  label: "First conversation",
                  render: (r) => (
                    <span style={{ display: "inline-flex", gap: 10, alignItems: "flex-start" }}>
                      <span className="aa-swatch" style={{ background: BUCKETS[r.bucket]?.color ?? "var(--aa-neutral)", marginTop: 4 }} />
                      <span>
                        <span style={{ fontWeight: 500 }}>{BUCKETS[r.bucket]?.label ?? words(r.bucket)}</span>
                        <span className="aa-sub">{BUCKETS[r.bucket]?.hint}</span>
                      </span>
                    </span>
                  ),
                },
                { key: "users", label: "People", align: "right", render: (r) => fmt(r.users) },
                { key: "rate7", label: "Back in 7d", align: "right", render: (r) => <Back rate={r.rate7} n={r.returned7} of={r.eligible7} /> },
                { key: "rate28", label: "Back in 28d", align: "right", render: (r) => <Back rate={r.rate28} n={r.returned28} of={r.eligible28} /> },
              ]}
              rows={retention.map((r) => ({ ...r, id: r.bucket }))}
            />
          ) : (
            <Empty compact icon="people" title="No new people in this period" />
          )}
        </Card>
      </div>

      <Card title="Cost and value by kind of request" subtitle="Each conversation counts once, under the kind most of its turns were" flush>
        {data?.perTask?.length ? (
          <Table
            defaultSort={{ key: "sessions", dir: "desc" }}
            columns={[
              { key: "task", label: "What they wanted", render: (r) => <span style={{ fontWeight: 500 }}>{TASK_LABELS[r.task] ?? words(r.task)}</span> },
              { key: "sessions", label: "Conversations", align: "right", render: (r) => fmt(r.sessions) },
              { key: "successRate", label: "Success", align: "right", render: (r) => <Rate value={r.successRate} status={statusOf(r.successRate, { good: 0.85, bad: 0.6 })} /> },
              { key: "costPerSession", label: "AI cost / conv.", align: "right", render: (r) => usd(r.costPerSession) },
              { key: "costPerSuccess", label: "AI cost / success", align: "right", render: (r) => usd(r.costPerSuccess) },
              { key: "creditsPerSession", label: "Credits / conv.", align: "right", render: (r) => fmt(r.creditsPerSession, 1) },
              { key: "recordsPerSession", label: "Records saved / conv.", align: "right", render: (r) => fmt(r.recordsPerSession, 1) },
            ]}
            rows={data.perTask.map((r) => ({ ...r, id: r.task }))}
          />
        ) : (
          <Empty compact icon="dollar" title="No reviewed conversations in this period" />
        )}
      </Card>
    </div>
  );
}

function Back({ rate, n, of }) {
  if (rate == null) return <span style={{ color: "var(--aa-faint)" }}>too recent</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }} title={`${n} of ${of}`}>
      <Meter value={rate} width={48} />
      <span style={{ minWidth: 36, fontWeight: 600 }}>{pct(rate)}</span>
    </span>
  );
}
